/**
 * SegmentTabs — the tab strip for a widget whose payload arrives split into
 * named segments (All / Med Precert / Rx PA, and the like).
 *
 * Kept generic rather than living inside one widget because more than one
 * chart type now receives the same segmented shape, and the selection
 * behaviour (first tab default, click to switch) is identical in each.
 */

interface Segment {
  /** Payload key — identifies the tab across re-renders. */
  key: string;
  /** Text shown on the tab. */
  label: string;
}

interface SegmentTabsProps {
  segments: Segment[];
  activeKey: string | undefined;
  onSelect: (key: string) => void;
  /** Names the tablist for screen readers — usually the widget title. */
  ariaLabel?: string;
}

export default function SegmentTabs({
  segments,
  activeKey,
  onSelect,
  ariaLabel,
}: SegmentTabsProps) {
  if (segments.length === 0) return null;

  return (
    <div className="segment-tabs" role="tablist" aria-label={ariaLabel}>
      {segments.map((segment) => {
        const isActive = segment.key === activeKey;
        return (
          <button
            key={segment.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`segment-tabs__tab${
              isActive ? " segment-tabs__tab--active" : ""
            }`}
            onClick={() => onSelect(segment.key)}
          >
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}
